/* eslint-disable @typescript-eslint/no-explicit-any */
import { Commit } from 'vuex';
import { PayinList } from '../interfaces/payins';
import { httpCommpix } from '@/util/HTTPCommpix';
import types from '../types/payins';

export interface QueryParams {
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
}

export type Pagination = {
  count: number;
  page: number;
  per_page: number;
};

export interface PayinsState {
  data: PayinList[];
  page: number;
  pageSize: number;
  totalRecords: number;
  loading: boolean;
}

const state: PayinsState = {
  data: [],
  page: 1,
  pageSize: 20,
  totalRecords: 0,
  loading: false,
};

const mutations = {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  [types.mutations.SET_PAYINS](
    state: PayinsState,
    payload: {
      data: PayinList[];
      page: number;
      pageSize: number;
      totalRecords: number;
    }
  ) {
    state.data = payload.data;
    state.page = payload.page;
    state.pageSize = payload.pageSize;
    state.totalRecords = payload.totalRecords;
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  [types.mutations.SET_LOADING](state: PayinsState, loading: boolean) {
    state.loading = loading;
  },
};

const actions = {
  [types.actions.PAYIN_LIST]: ({ commit, }: { commit: Commit }, queryParams: QueryParams): Promise<any> => {
    return new Promise((resolve, reject) => {
      commit(types.mutations.SET_LOADING, true);
      const { page = 1, pageSize = 20, filters = {}, } = queryParams;
      httpCommpix
        .get('/dashboard-central/history', {
          params: {
            page,
            pageSize,
            ...filters,
          },
        })
        .then((response) => {
          const {
            data,
            page: respPage,
            pageSize: respPageSize,
            total,
          } = response.data;
          response.data.pagination = {
            per_page: respPageSize,
            count: total,
            page: respPage,
          };
          commit(types.mutations.SET_PAYINS, {
            data,
            page: respPage,
            pageSize: respPageSize,
            totalRecords: total,
          });
          resolve(response);
        })
        .catch((error) => {
          commit(types.mutations.SET_PAYINS, {
            data: [],
            page: 1,
            pageSize: 20,
            totalRecords: 0,
          });
          reject(error);
        })
        .finally(() => {
          commit(types.mutations.SET_LOADING, false);
        });
    });
  },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async [types.actions.PAYIN_STATUS_LIST]({ commit, }: { commit: Commit }): Promise<any> {
    commit(types.mutations.SET_LOADING, true);
    try {
      const response = await httpCommpix.get('payment-status');
      const { data: statusOptions, } = response;
      return statusOptions;
    } catch (error) {
      return [];
    } finally {
      commit(types.mutations.SET_LOADING, false);
    }
  },
};

const getters = {
  [types.getters.GET_PAYINS]: (state: PayinsState): { data: PayinList[]; pagination: Pagination } => ({
    data: state.data,
    pagination: {
      count: state.totalRecords,
      page: state.page,
      per_page: state.pageSize,
    },
  }),
  [types.getters.GET_PAYIN_STATUS]: (state: PayinsState): { page: number; pageSize: number; totalRecords: number } => ({
    page: state.page,
    pageSize: state.pageSize,
    totalRecords: state.totalRecords,
  }),
  [types.getters.GET_PAYINS_PAGINATION]: (state: PayinsState): Pagination => ({
    count: state.totalRecords,
    page: state.page,
    per_page: state.pageSize,
  }),
  [types.getters.GET_LOADING]: (state: PayinsState): boolean => state.loading,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
